import * as React from "react";
import { createRef } from "react";
import { VerticalScrollbars } from "../scrollbars/VerticalScrollbars";
import { BaseList, InternalListProps, MandatoryCommonListProps } from "./BaseList";
import * as styles from "./PlainList.less";
import { getListHeightImp } from "./utils/listUtils";

export type PlainListProps<ItemType> = InternalListProps<ItemType> &
    MandatoryCommonListProps & {
        listMaxWidth?: number;
    };

interface PlainListState {
    pendingScrollIntoView: any;
}

export class PlainList<ItemType> extends BaseList<ItemType, PlainListProps<ItemType>, PlainListState> {
    listRef: React.RefObject<HTMLDivElement & VerticalScrollbars> = createRef();
    activeItem: HTMLDivElement;

    state = {
        pendingScrollIntoView: {},
    };

    calculateListHeight = () => {
        const { items, itemHeight, getItemHeight, height } = this.props;
        return getListHeightImp(items, itemHeight, getItemHeight, height);
    };

    scrollIntoView = (activeItem: HTMLDivElement) => {
        this.activeItem = activeItem;
        this.setState({ pendingScrollIntoView: {} });
    };

    rowExtraProps = { scrollIntoView: this.scrollIntoView };

    scrollIntoViewImpl() {
        const activeItem = this.activeItem;
        this.activeItem = null;

        const viewport = this.listRef.current;

        const viewPortTop = viewport.scrollTop;
        const viewportHeight = this.calculateListHeight();

        const elementTop = activeItem.offsetTop;
        const elementHeight = this.props.itemHeight;

        const currentScrollTop = viewport.scrollTop;

        if (elementTop + elementHeight >= viewPortTop + viewportHeight) {
            // scroll down
            const delta = elementTop + elementHeight - viewPortTop - viewportHeight;
            viewport.scrollTop = currentScrollTop + delta;
            return;
        }

        if (elementTop < viewPortTop) {
            // scroll up
            viewport.scrollTop = currentScrollTop - viewPortTop + elementTop;
            return;
        }
    }

    render() {
        const { listMaxWidth, withAutoRowItemHeight = false } = this.props;

        const listStyleObj: React.CSSProperties = {};
        if (!withAutoRowItemHeight) {
            listStyleObj.height = this.calculateListHeight();
        }

        if (listMaxWidth) {
            listStyleObj.maxWidth = `${listMaxWidth}px`;
        }

        const items = this.props.items.map((item, index) =>
            this.rowRenderer({
                index,
                key: this.props.getItemKeyForRender?.(item) ?? String(index),
            })
        );

        if (this.props.customScrollbar) {
            return (
                <VerticalScrollbars
                    ref={this.listRef}
                    wrapperProps={{
                        className: this.props.customScrollbarWrapperClassName || styles.wrapper,
                        style: listStyleObj,
                    }}
                    initPreventWheel={true}
                >
                    {items}
                </VerticalScrollbars>
            );
        }

        return (
            <div style={listStyleObj} className={styles.wrapper} ref={this.listRef} role="listbox">
                {items}
            </div>
        );
    }

    componentDidUpdate(prevProps: Readonly<PlainListProps<ItemType>>, prevState: Readonly<PlainListState>) {
        if (prevState.pendingScrollIntoView !== this.state.pendingScrollIntoView && this.listRef.current) {
            this.scrollIntoViewImpl();
        }
    }
}
